import convert from 'convert';

import { roundNumberTo } from '../numbers';
import { Nil } from '../type-utils';

import { convertMeterToDisplayUnit } from './motionConvertor';
import { getDisplayUnitAndSymbol, Unit } from './unitMapping';

export type LengthDisplayUnits = 'in' | 'cm';
export type LengthDisplaySymbols = '"' | 'cm';

export type AngleDisplayUnits = 'deg' | 'deg/s' | 'deg/s^2' | 'deg^2/s';
export type AngleDisplaySymbols = '°' | '°/s' | '°/s²' | '°²/s';

export type AccelerationDisplayUnits = 'cm/s^2' | 'in/s^2';
export type AccelerationDisplaySymbols = '"/s²' | 'cm/s²';

export type SpeedDisplayUnits = 'km/h' | 'cm/s' | 'mph' | 'in/s';
export type SpeedDisplaySymbol = '"/s' | 'cm/s' | 'km/h' | 'mph';

export type RatioDisplayUnit = 'percent';
export type RatioDisplaySymbol = '%';

export type TimeUnit = 's';
export type TimeSymbol = 's';

export type UnknownUnit = '-';
export type UnknownSymbol = '-';

export type DisplayUnit =
    | RatioDisplayUnit
    | SpeedDisplayUnits
    | AccelerationDisplayUnits
    | LengthDisplayUnits
    | AngleDisplayUnits
    | TimeUnit
    | UnknownUnit;

export type DisplaySymbol =
    | RatioDisplaySymbol
    | SpeedDisplaySymbol
    | AccelerationDisplaySymbols
    | LengthDisplaySymbols
    | AngleDisplaySymbols
    | TimeSymbol
    | UnknownSymbol;

export const DisplayUnitMap: Record<DisplayUnit, DisplayUnit> = {
    in: 'in',
    cm: 'cm',
    deg: 'deg',
    'deg/s': 'deg/s',
    'deg/s^2': 'deg/s^2',
    'deg^2/s': 'deg^2/s',
    'cm/s^2': 'cm/s^2',
    'in/s^2': 'in/s^2',
    'km/h': 'km/h',
    'cm/s': 'cm/s',
    mph: 'mph',
    'in/s': 'in/s',
    percent: 'percent',
    s: 's',
    '-': '-',
};

export const isDisplayUnitInInches = (unit: DisplayUnit): boolean => {
    return unit === 'in' || unit === 'in/s' || unit === 'in/s^2';
};

export const isDisplayUnit = (value: unknown): value is DisplayUnit =>
    typeof value === 'string' && value in DisplayUnitMap;

export const DisplaySymbolMap: Record<DisplayUnit, DisplaySymbol> = {
    in: '"',
    cm: 'cm',
    deg: '°',
    'deg/s': '°/s',
    'deg/s^2': '°/s²',
    'deg^2/s': '°²/s',
    'cm/s^2': 'cm/s²',
    'in/s^2': '"/s²',
    'km/h': 'km/h',
    'cm/s': 'cm/s',
    mph: 'mph',
    'in/s': '"/s',
    percent: '%',
    s: 's',
    '-': '-',
};

export const isDisplaySymbolInDegrees = (value: unknown): boolean =>
    value === DisplaySymbolMap['deg'] || value === DisplaySymbolMap['deg/s'] || value === DisplaySymbolMap['deg/s^2'];

export type UserPreferredUnit = 'metric' | 'imperial' | 'mixed';

export const UserPreferredUnitMap: Record<UserPreferredUnit, UserPreferredUnit> = {
    metric: 'metric',
    imperial: 'imperial',
    mixed: 'mixed',
};
const DEFAULT_ROUNDING = 1;
const unitRoundingMap: Partial<Record<Unit, number>> = {
    m: 1,
    'm/s': 1,
    'm/s^2': 1,
    rad: 0,
    'rad/s': 0,
    'rad/s^2': 0,
    'rad^2/s': 0,
    ratio: 1,
    s: 2,
};

const displayUnitToUnitMap: Record<DisplayUnit, Unit> = {
    in: 'm',
    cm: 'm',
    deg: 'rad',
    'deg/s': 'rad/s',
    'deg/s^2': 'rad/s^2',
    'deg^2/s': 'rad^2/s',
    'cm/s^2': 'm/s^2',
    'in/s^2': 'm/s^2',
    'km/h': 'm/s',
    'cm/s': 'm/s',
    mph: 'm/s',
    'in/s': 'm/s',
    percent: 'ratio',
    s: 's',
    '-': '-',
};

type RoundToArgs = { value: number | Nil; displayUnit: DisplayUnit; rounding?: number | Nil };

export function roundDisplayValueTo(args: RoundToArgs & { value: number }): number;
export function roundDisplayValueTo(args: RoundToArgs & { value: null }): null;
export function roundDisplayValueTo(args: RoundToArgs & { value: undefined }): undefined;
export function roundDisplayValueTo(args: RoundToArgs): number | Nil;
export function roundDisplayValueTo({ value, displayUnit, rounding: roundingOverride }: RoundToArgs): number | Nil {
    if (roundingOverride === 0) return value;

    const unit = displayUnitToUnitMap[displayUnit];
    const rounding = unitRoundingMap[unit] ?? (unit === '-' ? DEFAULT_ROUNDING : null);
    return roundNumberTo(value, roundingOverride ?? rounding);
}

type ConversionArgs = {
    value: number | Nil;
    unit: Unit;
    userPreferredUnit?: UserPreferredUnit;
    convertToHour?: boolean;
    rounding?: number | Nil;
};

type UnitConversionReturnType = {
    value: number | Nil;
    unit: DisplayUnit;
    symbol: DisplaySymbol;
};

export function simpleConvert(args: ConversionArgs & { value: number }): number;
export function simpleConvert(args: ConversionArgs & { value: null }): null;
export function simpleConvert(args: ConversionArgs & { value: number | Nil }): number | Nil;
export function simpleConvert(args: ConversionArgs & { value: undefined }): undefined;
export function simpleConvert({
    value,
    userPreferredUnit = UserPreferredUnitMap.metric,
    convertToHour = true,
    unit,
    rounding,
}: ConversionArgs): number | Nil {
    const imperial = userPreferredUnit === UserPreferredUnitMap.imperial;
    const metric = userPreferredUnit === UserPreferredUnitMap.metric;
    const { unit: displayUnit } = getDisplayUnitAndSymbol(unit, userPreferredUnit, convertToHour);
    switch (unit) {
        case 'm': {
            const convertedValue = value ? convert(value, 'm').to(imperial ? 'in' : 'cm') : value;
            return roundDisplayValueTo({ value: convertedValue, displayUnit, rounding });
        }
        case 'rad':
        case 'rad/s':
        case 'rad/s^2': {
            const convertedValue = value ? convert(value, 'rad').to('deg') : value;
            return roundDisplayValueTo({ value: convertedValue, displayUnit, rounding });
        }
        case 'ratio': {
            const convertedValue = value ? value * 100 : value;
            return roundDisplayValueTo({ value: convertedValue, displayUnit, rounding });
        }
        case 'm/s': {
            if (convertToHour) {
                const val = convertMeterToDisplayUnit(value, metric ? 'km/h' : 'mph');
                return roundDisplayValueTo({ value: val, displayUnit, rounding });
            }
            const val = convertMeterToDisplayUnit(value, !imperial ? 'cm/s' : 'in/s');
            return roundDisplayValueTo({ value: val, displayUnit, rounding });
        }
        case 'm/s^2': {
            const convertedValue = convertMeterToDisplayUnit(value, !imperial ? 'cm/s^2' : 'in/s^2');
            return roundDisplayValueTo({ value: convertedValue, displayUnit, rounding });
        }
        case 's': {
            return roundDisplayValueTo({ value, displayUnit, rounding });
        }
        case 'rad^2/s': {
            const convertedValue = value ? value * convert(1, 'rad').to('deg') ** 2 : value;
            return roundDisplayValueTo({ value: convertedValue, displayUnit, rounding });
        }
        default: {
            return roundNumberTo(value, rounding === 0 ? null : rounding ?? DEFAULT_ROUNDING);
        }
    }
}

export function convertUnit(args: ConversionArgs & { value: number }): UnitConversionReturnType & { value: number };
export function convertUnit(args: ConversionArgs & { value: null }): UnitConversionReturnType & { value: null };
export function convertUnit(
    args: ConversionArgs & { value: number | Nil },
): UnitConversionReturnType & { value: number | Nil };
export function convertUnit(
    args: ConversionArgs & { value: undefined },
): UnitConversionReturnType & { value: undefined };
export function convertUnit({
    value,
    userPreferredUnit = UserPreferredUnitMap.metric,
    convertToHour = true,
    unit,
    rounding,
}: ConversionArgs): UnitConversionReturnType {
    switch (unit) {
        case 'm':
        case 'rad':
        case 'rad/s':
        case 'rad/s^2':
        case 'rad^2/s':
        case 'ratio':
        case 'm/s^2':
        case 's': {
            return {
                value: simpleConvert({ value, userPreferredUnit, unit, rounding }),
                ...getDisplayUnitAndSymbol(unit, userPreferredUnit),
            };
        }
        case 'm/s': {
            return {
                value: simpleConvert({ value, userPreferredUnit, unit, convertToHour, rounding }),
                ...getDisplayUnitAndSymbol(unit, userPreferredUnit, convertToHour),
            };
        }
        default: {
            return {
                value: roundNumberTo(value, rounding === 0 ? null : rounding ?? DEFAULT_ROUNDING),
                ...getDisplayUnitAndSymbol(unit, userPreferredUnit),
            };
        }
    }
}
